/**
 * @file src/api/collectionArrangementService.js
 * @description A PERSON's own arrangement of a collection — the stages and rail THEY left.
 *
 * ## Two authors, two homes, one shape
 *
 * A collection carries its own arrangement in its saved layout, written by its EDITORS and read by
 * everyone who can view it: the collection saying what it IS. That leaves a person who cannot edit
 * a collection with nowhere to keep the way they left it — they arrange the stages, come back, and
 * the studio has forgotten, while an editor's identical gesture is remembered.
 *
 * So: you write your arrangement wherever you have the right to. An editor's IS the collection's
 * and goes to the collection layout (`collectionDataService.js`). Anyone else's is their own and
 * comes here, filed per person on their account. A reader takes their own if they have one and the
 * collection's if they do not — never a merge of the two, because merging two sets of POSITIONS
 * puts a card back on a stage the person moved it off.
 *
 * The two records hold the same fields and go through the same sanitizer server-side, so the studio
 * resolves either one with the same code.
 *
 * ## Why it is on the account rather than in this browser
 *
 * A `local:` file is per-machine because its bytes are. An arrangement is not — it is a per-PERSON
 * fact, and kept in browser storage it would be gone on your phone, gone in a second browser, and
 * gone when site data is cleared. Kept on the account it also cannot go stale against itself, which
 * is what a per-machine copy does the moment you arrange the same collection somewhere else.
 *
 * This module is the ONLY one allowed to name this endpoint — see
 * `tests/config/configurationEndpointOwnership.test.js`. Everything else goes through the functions
 * here, so every read and every accepted write reaches the offline mirror.
 */
import { fetchJsonFromApi } from './httpClient.js';
import { createArrangementRevisions } from './arrangementRevisions.js';
import { requestEmbeddedAuthToken } from './dataManager/loaders.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';
import {
  CONFIGURATION_KINDS,
  READ_OUTCOME,
  beginConfigurationOperation,
  configurationEpoch,
  isConfigurationEpochCurrent,
  mirrorAccepted,
  readThrough,
} from '../config/configurationPack.js';

const ENDPOINT = '/me/users/configurations/collection-arrangement';

/**
 * The write bookkeeping for THIS person's arrangements — what the server last confirmed each one
 * holds, and which revision that was — so a save states only what changed and cannot be overtaken
 * by an older one. See `arrangementRevisions.js`. Its own tracker, separate from the collection's
 * layout: a different document, with its own revision line.
 */
const arrangementWrites = createArrangementRevisions();

/**
 * Which record a write is bookkept under: this PERSON's arrangement of this collection.
 *
 * The person is part of the key, not just the collection. A personal arrangement is a different
 * document for each person, so a base one of them confirmed describes nothing for the next: someone
 * who signs out and another who signs in on the same page would otherwise have their first save
 * measured as a delta against the previous person's arrangement. The identity epoch changes exactly
 * when the person does, and it is readable synchronously — which the leaving write requires, since
 * nothing may be awaited before it is issued.
 */
const writeKey = (epoch, collectionId) => `${epoch}:${collectionId}`;

const urlFor = (collectionId) => `${ENDPOINT}/${encodeURIComponent(collectionId)}`;

/**
 * What one GET actually found, in the terms `configurationPack` reasons about.
 *
 * The distinction that matters here is between "you have never arranged this collection" and "the
 * read did not get through". The first is authoritative and means fall back to the collection's own
 * arrangement; the second means change nothing, on disk or on screen. Collapsed together, one
 * flaky boot would look exactly like a person choosing to go back to the collection's arrangement.
 *
 * @param {Response} response
 * @returns {Promise<{outcome: string, value: object|null, message: string|null}>}
 */
async function readLoadResponse(response) {
  if (response.status === 204 || response.status === 404) {
    // 404 is the collection, not the arrangement: deleted, or no longer visible to this person.
    // Authoritative all the same — an arrangement of a collection they can no longer open is not
    // worth keeping on disk, and the studio will not be showing it either.
    return { outcome: READ_OUTCOME.ABSENT, value: null, message: null };
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
      return { outcome: READ_OUTCOME.UNAUTHORIZED, value: null, message: null };
    }
    return {
      outcome: READ_OUTCOME.UNAVAILABLE,
      value: null,
      message: `Failed to load the saved arrangement (status ${response.status})`,
    };
  }

  let body;
  try {
    body = await response.json();
  } catch {
    return { outcome: READ_OUTCOME.INVALID_RESPONSE, value: null, message: null };
  }

  // The server states the absence explicitly rather than 404ing it, because a person with no
  // arrangement of a collection they CAN see is a different answer from a collection they cannot.
  const layout = body?.arrangement?.layout ?? null;
  if (!layout || typeof layout !== 'object') {
    return { outcome: READ_OUTCOME.ABSENT, value: null, message: null };
  }
  return { outcome: READ_OUTCOME.NETWORK, value: layout, message: null };
}

/**
 * This person's own arrangement of one collection, or null when they have none.
 *
 * Never throws and never blocks a boot: an unreachable server answers `unavailable`, the mirror is
 * served if it holds anything, and the studio falls back to the collection's own arrangement. That
 * fallback is also exactly what a client sees before the endpoint exists, which is what makes
 * shipping this app ahead of the backend safe rather than broken.
 *
 * @param {string} collectionId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object|null>} the stored layout, or null
 */
export async function readCollectionArrangement(collectionId, { signal } = {}) {
  if (!collectionId) return null;
  const epoch = configurationEpoch();
  const answer = await readThrough({
    kind: CONFIGURATION_KINDS.ARRANGEMENT,
    resourceKey: String(collectionId),
    read: async () => {
      try {
        const { authToken } = await resolveViewerAuth();
        const response = await fetchJsonFromApi(urlFor(collectionId), { method: 'GET', authToken, signal });
        return readLoadResponse(response);
      } catch (error) {
        const outcome = error?.name === 'AbortError' ? READ_OUTCOME.ABORTED : READ_OUTCOME.UNAVAILABLE;
        return { outcome, value: null, message: error?.message || null };
      }
    },
  });
  const layout = answer?.value ?? null;
  // Start this session's write counter above the revision the stored arrangement is at, so its
  // first save is not mistaken for one made before it. Only the NUMBER is taken: this answer may
  // have come from the offline mirror, which can be behind the server, and no delta may ever be
  // measured against a base the server has not confirmed to this session.
  // ...and only for the person it was read for. An answer that lands after somebody else has
  // arrived describes an arrangement that is not theirs.
  if (isConfigurationEpochCurrent(epoch)) arrangementWrites.observeStored(writeKey(epoch, collectionId), layout);
  return layout;
}

/**
 * Save this person's own arrangement of a collection.
 *
 * Fire-and-forget by design: the arrangement is already live on screen, and a save that cannot go
 * out must not undo a drag or hold up the studio. It reconciles on the next boot's read.
 *
 * ## It states what CHANGED, not the whole arrangement
 *
 * A save carries only the fields that differ from the arrangement the server last confirmed (see
 * `arrangementRevisions.js`), rather than re-sending a whole card rail — up to 500 ids — to say that
 * one divider moved. A delta is a PATCH to the same path: a different VERB, so a deployment that
 * predates it refuses the request instead of misreading the delta as a whole arrangement. Every write carries a REVISION,
 * and the server applies one only when it is at least the revision it already holds, so two saves in
 * flight can no longer land in the wrong order and let the older one win.
 *
 * This is not the merge the record's shape forbids. A merge of two ARRANGEMENTS puts a card back on
 * a stage its owner moved it off; a delta is one arrangement, described against the base the server
 * itself confirmed, and any positional field it changes it re-states whole.
 *
 * The mirror is written after the SERVER HAS ACCEPTED, not before — nothing GETs after a PUT, so a
 * mirror fed by reads alone would be stale from the first card anybody moves. It is written with the
 * WHOLE arrangement, never the delta: it is what a boot with no network reads, so half a record is
 * worse there than none.
 *
 * @param {string} collectionId
 * @param {object} layout the arrangement as it stands — the same shape the collection's own layout has
 * @param {{ immediate?: boolean }} [options] `immediate` is the LEAVING path (see below)
 * @returns {Promise<boolean>} whether the server accepted it
 */
export async function saveCollectionArrangement(collectionId, layout, { immediate = false } = {}) {
  if (!collectionId || !layout || typeof layout !== 'object') return false;
  const epoch = configurationEpoch();
  const key = writeKey(epoch, collectionId);

  const write = arrangementWrites.begin(key, layout, { whole: immediate });
  // Nothing differs from what the server confirmed — a divider that settled back where it started,
  // or a geometry callback with nothing new in it. Already saved.
  if (!write) return true;

  // The studio is unloading. Two things change, both required for the write to survive the
  // navigation that triggered it — the same trade the collection's own layout write makes:
  //   - `keepalive`, so the browser finishes the request after the document is gone;
  //   - NOTHING is awaited before the fetch. Resolving the bearer and settling who this is for are
  //     both asynchronous, and a request still sitting in a microtask when the document unloads is
  //     simply dropped — `keepalive` cannot save a request that was never issued. Mycelium accepts
  //     the session cookie on its own, so the bearer is what is given up rather than the write.
  // The mirror is skipped for the same reason: an IndexedDB write cannot finish during an unload,
  // and the next boot reads the server anyway. The ANSWER is given up too — reading a body means
  // awaiting it — but nothing follows this write, so no later delta needs the base it would confirm.
  if (immediate) {
    try {
      const response = await fetchJsonFromApi(urlFor(collectionId), {
        authToken: null,
        keepalive: true,
        method: write.partial ? 'PATCH' : 'PUT',
        body: { layout: write.layout },
      });
      return Boolean(response?.ok);
    } catch {
      return false;
    }
  }

  return sendArrangement(collectionId, key, epoch, write, false);
}

/**
 * Issue one arrangement write, settle this side's bookkeeping, and mirror what the server accepted.
 *
 * Split out so the one CORRECTION this path allows itself has somewhere to go: a write the server
 * refuses as stale — another device holds a newer arrangement — is re-issued once, above the
 * revision the server named and stating the whole arrangement again, because the base it was
 * measured against has just been shown to be wrong. A single answer to an authoritative "you were
 * behind", not a queue: chaining writes would let one hung request stall every save behind it.
 */
async function sendArrangement(collectionId, key, epoch, write, corrected) {
  // Captured BEFORE anything is issued: an answer that lands after somebody else has arrived at
  // this browser belongs to the person it was sent for, and must not be filed under whoever is
  // here now.
  const identity = await beginConfigurationOperation();
  // The person changed between this save being opened and being sent. What was opened is the
  // previous person's arrangement, and the bearer below would be the new person's — sending it would
  // file one person's arrangement on another's account.
  if (identity.epoch !== epoch) return false;
  try {
    const { authToken } = await resolveViewerAuth();
    const response = await fetchJsonFromApi(urlFor(collectionId), {
      // A delta is a PATCH to the same path, which a deployment that predates it does not answer.
      // That refusal is the point: it cannot misread the delta as a whole arrangement and store it
      // as one, so the two sides may be deployed — or rolled back — in either order.
      method: write.partial ? 'PATCH' : 'PUT',
      authToken,
      body: { layout: write.layout },
    });
    // Somebody else arrived while this was in flight. Nothing about the answer may be kept — and,
    // more to the point, nothing may be RE-ISSUED: a correction or a fallback sent now would carry
    // the previous person's arrangement under the new person's bearer.
    if (!isConfigurationEpochCurrent(epoch)) return false;
    if (!response.ok) {
      arrangementWrites.fail(key, write);
      // This server has no PATCH for this record: it predates deltas, or has been rolled back behind
      // them. Nothing was stored and nothing was lost — so the arrangement is re-stated whole.
      if (write.partial && (response.status === 404 || response.status === 405)) {
        const replacement = arrangementWrites.begin(key, write.full, { whole: true });
        if (!replacement) return false;
        return sendArrangement(collectionId, key, epoch, replacement, corrected);
      }
      return false;
    }

    const body = await response.json().catch(() => null);
    // Reading the body is another await, and so another window for somebody else to arrive in.
    if (!isConfigurationEpochCurrent(epoch)) return false;
    if (body?.applied === false) {
      const stale = arrangementWrites.reject(key, write, body?.rev ?? null);
      // A refusal naming NO revision says the row is not there at all — this person went back to the
      // collection's arrangement, or an editor's own layout save retired it. Re-creating it would
      // undo a deliberate act, so the correction is exactly what must not happen here.
      if (body?.rev === null || body?.rev === undefined) {
        arrangementWrites.unconfirm(key);
        return false;
      }
      if (!stale || corrected) return false;
      const next = arrangementWrites.begin(key, write.full, { whole: true });
      if (!next) return false;
      return sendArrangement(collectionId, key, epoch, next, true);
    }

    // The arrangement AS STORED becomes the base for the next delta, and the value the mirror keeps
    // — not what this side believed it sent. When another device has moved the record, those differ,
    // and a mirror holding a value the server never had is what a boot with no network would read.
    const stored = body?.arrangement?.layout ?? null;
    arrangementWrites.confirm(key, write, stored);
    // Only if no LATER write has already reached the mirror: two accepted writes arrive here in
    // whatever order their responses do, and the older one would otherwise leave the mirror holding
    // an arrangement the server has already moved past.
    if (stored && arrangementWrites.claimMirror(key, write)) {
      await mirrorAccepted({
        kind: CONFIGURATION_KINDS.ARRANGEMENT,
        resourceKey: String(collectionId),
        value: stored,
        identity,
      }).catch(() => {});
    }
    return true;
  } catch {
    arrangementWrites.fail(key, write);
    return false;
  }
}

/**
 * Drop this browser's copy of a personal arrangement the SERVER has already removed.
 *
 * The editor path retires the row inside the collection-layout save itself, server-side, so there
 * is no request to make here — but the offline mirror does not know that. Left alone it is only
 * cleared when a later GET answers `absent`, and a boot with no network never gets that far: the
 * mirror is served instead, and the arrangement the editor retired shadows the collection layout
 * they had just saved. Exactly the state the atomic delete exists to prevent, one layer down.
 *
 * @param {string} collectionId
 */
export async function forgetLocalCollectionArrangement(collectionId) {
  if (!collectionId) return false;
  // The write bookkeeping goes with it. A confirmed base and a revision left over from a row that
  // has been retired describe a document that is gone, and the next personal write would measure a
  // delta against it — landing a partial arrangement on a record that has to be created whole.
  arrangementWrites.forget(writeKey(configurationEpoch(), collectionId));
  const identity = await beginConfigurationOperation();
  if (!isConfigurationEpochCurrent(identity.epoch)) return false;
  // Recorded as an accepted NULL rather than deleted: `mirrorAccepted` is the one way a value
  // reaches both the page memo and the row on disk, and both have to forget this together.
  return mirrorAccepted({
    kind: CONFIGURATION_KINDS.ARRANGEMENT,
    resourceKey: String(collectionId),
    value: null,
    identity,
  }).catch(() => false);
}

/**
 * Forget this person's own arrangement, so from the next boot they see the collection's again.
 *
 * Two callers, and the second is what makes this load-bearing rather than a convenience:
 *
 *   - a deliberate "use the collection's arrangement". No button ships today — the studio has
 *     enough of them, and nothing has asked for it yet. This function IS that button's handler:
 *     wiring one up is a menu item calling this, and nothing else has to change.
 *   - an EDITOR saving the collection's layout. Their arrangement IS the collection's, so having
 *     just said what the collection is, a personal override they were still carrying would shadow
 *     it for ever. That is the person who arranged a collection as a viewer and was later given
 *     edit rights, and without this their own studio would stop following the collection silently.
 *
 * @param {string} collectionId
 * @returns {Promise<boolean>} whether the server accepted it
 */
export async function forgetCollectionArrangement(collectionId, { immediate = false } = {}) {
  if (!collectionId) return false;
  // Same reason as above: the row this side knew the revision of is being removed.
  arrangementWrites.forget(writeKey(configurationEpoch(), collectionId));

  // The leaving path, for the same reason the save has one: nothing may be awaited before the
  // request is issued or the document unloads with it still in a microtask. Without this an editor
  // leaving the studio saved the collection's arrangement but never removed their own, and their
  // own would shadow it on the next boot — the exact state this call exists to prevent.
  if (immediate) {
    try {
      const response = await fetchJsonFromApi(urlFor(collectionId), {
        method: 'DELETE',
        authToken: null,
        keepalive: true,
      });
      return Boolean(response?.ok);
    } catch {
      return false;
    }
  }

  const identity = await beginConfigurationOperation();
  try {
    const { authToken } = await resolveViewerAuth();
    const response = await fetchJsonFromApi(urlFor(collectionId), { method: 'DELETE', authToken });
    if (!response.ok) return false;
    if (!isConfigurationEpochCurrent(identity.epoch)) return false;
    // Mirrored as an accepted NULL rather than left alone: the row on disk is what a boot with no
    // network reads, and one still holding the arrangement just removed would put it straight back.
    await mirrorAccepted({
      kind: CONFIGURATION_KINDS.ARRANGEMENT,
      resourceKey: String(collectionId),
      value: null,
      identity,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}
